/**
 * Notification History Modal
 *
 * Opened from "View history" in the bell dropdown. Pages back through older
 * notifications on demand (useNotificationHistory), grouped by day. Shows only
 * what the user can access and has enabled — the same rule as the bell.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import type { NotificationItem } from '@/types/notification.types';
import { BaseModal } from './BaseModal';
import { LoadingSpinner } from './LoadingSpinner';
import { NotificationListItem } from './NotificationListItem';
import { useNotificationHistory } from '../hooks/useNotificationHistory';
import { useFirebaseStore } from '../store/firebaseStore';
import { groupNotificationsByDay } from '../utils/notificationHistory';

interface NotificationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Same handler as a bell click: marks read, switches location, navigates */
  onNotificationClick: (notification: NotificationItem) => void;
}

export const NotificationHistoryModal: React.FC<NotificationHistoryModalProps> = ({
  isOpen,
  onClose,
  onNotificationClick,
}) => {
  const { currentUser } = useFirebaseStore();
  const { items, isLoading, error, hasMore, refresh, loadOlder } = useNotificationHistory();

  // Load the newest page each time the modal opens. Read refresh via ref so a
  // currentUser update while open doesn't wipe the pages already loaded.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (isOpen) refreshRef.current();
  }, [isOpen]);

  const groups = useMemo(() => groupNotificationsByDay(items), [items]);
  const userEmail = currentUser?.email || '';

  const handleItemClick = (notification: NotificationItem) => {
    onClose();
    onNotificationClick(notification);
  };

  const footer = (
    <div className="flex justify-between gap-3">
      <button
        onClick={refresh}
        disabled={isLoading}
        className="px-4 py-2 border border-line-strong rounded-lg text-ink-body hover:bg-surface-muted font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
      >
        <i className="fa-solid fa-rotate-right"></i>
        Refresh
      </button>
      <button
        onClick={onClose}
        className="px-4 py-2 border border-line-strong rounded-lg text-ink-body hover:bg-surface-muted font-medium transition-colors"
      >
        Close
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Notification History"
      subtitle="Notifications for the pages and locations you've enabled"
      icon="fa-solid fa-clock-rotate-left"
      maxWidth="lg"
      fullScreenOnMobile
      footer={footer}
    >
      {items.length === 0 && isLoading ? (
        <div className="py-12">
          <LoadingSpinner message="Loading history..." />
        </div>
      ) : items.length === 0 && !error ? (
        <div className="py-12 text-center text-ink-muted">
          <i className="fa-regular fa-bell-slash text-3xl mb-2 opacity-50"></i>
          <p>No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.key}>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2">
                {group.label}
              </h3>
              <div className="rounded-xl border border-line overflow-hidden [&>*:last-child]:border-b-0">
                {group.items.map((notification) => (
                  <NotificationListItem
                    key={notification.id}
                    notification={notification}
                    isRead={notification.readBy.includes(userEmail)}
                    onClick={handleItemClick}
                  />
                ))}
              </div>
            </section>
          ))}

          {error && (
            <div className="rounded-lg bg-danger-soft text-danger-strong px-4 py-3 text-sm flex items-center justify-between gap-3">
              <span>{error}</span>
              <button
                onClick={items.length === 0 ? refresh : loadOlder}
                className="font-medium underline"
              >
                Try again
              </button>
            </div>
          )}

          {!error && items.length > 0 && (
            <div className="text-center pt-1">
              {hasMore ? (
                <button
                  onClick={loadOlder}
                  disabled={isLoading}
                  className="text-sm font-medium transition-colors text-accent-strong hover:underline disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {isLoading && <i className="fa-solid fa-spinner fa-spin"></i>}
                  {isLoading ? 'Loading...' : 'Load older'}
                </button>
              ) : (
                <p className="text-xs text-ink-muted">No older notifications</p>
              )}
            </div>
          )}
        </div>
      )}
    </BaseModal>
  );
};
